import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Loader2, ExternalLink, Pencil, Trash2, CalendarDays } from "lucide-react";

const inputCls = "w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#1A365D] focus:border-transparent transition-all outline-none";
const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const emptyForm = { property_id: "", guest_name: "", guest_email: "", guest_phone: "", check_in: "", check_out: "", num_guests: 1, total_price: 0, status: "confirmed", payment_status: "unpaid", godaddy_invoice_url: "", notes: "" };

const statusBadge = { confirmed: "bg-[#1A365D] text-white", pending: "bg-[#F59E0B] text-white", cancelled: "bg-gray-300 text-gray-700" };
const payBadge = { paid: "bg-[#81B29A] text-white", invoiced: "bg-[#E07A5F] text-white", unpaid: "bg-gray-200 text-gray-600" };

export default function Bookings() {
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/bookings"), api.get("/properties")]).then(([b, p]) => {
      setItems(b.data); setProps(p.data);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const propName = (id) => props.find((p) => p.id === id)?.name || "—";
  const propPrice = (id) => props.find((p) => p.id === id)?.price_per_night || 0;

  const openNew = () => { setEditing(null); setForm({ ...emptyForm, property_id: props[0]?.id || "" }); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...b }); setOpen(true); };

  // auto compute total when dates/property change for new bookings
  const recalc = (next) => {
    if (next.check_in && next.check_out && next.property_id) {
      const nights = (new Date(next.check_out) - new Date(next.check_in)) / 86400000;
      if (nights > 0) next.total_price = Math.round(nights * propPrice(next.property_id) * 100) / 100;
    }
    return next;
  };

  const setField = (k, v) => setForm((f) => recalc({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, num_guests: Number(form.num_guests), total_price: Number(form.total_price) };
    try {
      if (editing) await api.put(`/bookings/${editing.id}`, payload);
      else await api.post("/bookings", payload);
      toast.success(editing ? "Booking updated" : "Booking created");
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete booking for ${b.guest_name}?`)) return;
    await api.delete(`/bookings/${b.id}`); toast.success("Booking deleted"); load();
  };

  return (
    <div data-testid="bookings-page" className="max-w-6xl">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-2">Reservations</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none font-medium">Bookings</h1>
        </div>
        <button data-testid="add-booking-button" onClick={openNew} disabled={props.length === 0} className="bg-[#1A365D] text-white hover:bg-[#2A4A7F] rounded-full px-5 py-2.5 font-medium transition-colors flex items-center gap-2 disabled:opacity-50">
          <Plus size={18} /> New Booking
        </button>
      </div>

      {props.length === 0 && !loading && (
        <p className="text-sm text-[#E07A5F] mb-6">Add a property first to create bookings.</p>
      )}

      {loading ? (
        <div className="flex justify-center pt-20"><Loader2 className="animate-spin text-[#1A365D]" size={28} /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-gray-400">No bookings yet.</div>
      ) : (
        <div className="w-full overflow-hidden rounded-2xl border border-black/5 bg-white shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F4F1EA] text-[#1A365D] text-xs uppercase tracking-wider">
                <th className="py-4 px-6 text-left font-semibold">Guest</th>
                <th className="py-4 px-6 text-left font-semibold">Property</th>
                <th className="py-4 px-6 text-left font-semibold">Dates</th>
                <th className="py-4 px-6 text-left font-semibold">Total</th>
                <th className="py-4 px-6 text-left font-semibold">Status</th>
                <th className="py-4 px-6 text-left font-semibold">Payment</th>
                <th className="py-4 px-6"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-black/5 hover:bg-[#FDFBF7] transition-colors">
                  <td className="py-4 px-6 text-sm font-medium text-gray-800">{b.guest_name}<div className="text-xs text-gray-400">{b.guest_email}</div></td>
                  <td className="py-4 px-6 text-sm text-gray-600">{propName(b.property_id)}</td>
                  <td className="py-4 px-6 text-sm text-gray-600">{fmt(b.check_in)} → {fmt(b.check_out)}</td>
                  <td className="py-4 px-6 text-sm font-mono text-[#1A365D]">${b.total_price}</td>
                  <td className="py-4 px-6"><span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadge[b.status]}`}>{b.status}</span></td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${payBadge[b.payment_status]}`}>{b.payment_status}</span>
                      {b.godaddy_invoice_url && (
                        <a data-testid={`pay-invoice-${b.id}`} href={b.godaddy_invoice_url} target="_blank" rel="noreferrer" className="text-[#E07A5F] hover:underline text-xs flex items-center gap-0.5" title="Pay via GoDaddy invoice">
                          <ExternalLink size={13} /> Invoice
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex gap-1 justify-end">
                      <button data-testid={`edit-booking-${b.id}`} onClick={() => openEdit(b)} className="p-2 rounded-full text-gray-400 hover:text-[#1A365D] hover:bg-[#F4F1EA] transition-colors"><Pencil size={15} /></button>
                      <button data-testid={`delete-booking-${b.id}`} onClick={() => remove(b)} className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-[#EF4444] transition-colors"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1A365D]/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div data-testid="booking-dialog" className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 shadow-xl animate-in fade-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-3xl font-medium">{editing ? "Edit Booking" : "New Booking"}</h2>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700"><X size={22} /></button>
            </div>
            <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Property</label>
                <select data-testid="booking-property-select" required className={inputCls} value={form.property_id} onChange={(e) => setField("property_id", e.target.value)}>
                  <option value="">Select property…</option>
                  {props.map((p) => <option key={p.id} value={p.id}>{p.name} — ${p.price_per_night}/nt</option>)}
                </select>
              </div>
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Guest name</label><input data-testid="booking-guest-input" required className={inputCls} value={form.guest_name} onChange={(e) => setField("guest_name", e.target.value)} /></div>
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Guest email</label><input type="email" className={inputCls} value={form.guest_email} onChange={(e) => setField("guest_email", e.target.value)} /></div>
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Phone</label><input className={inputCls} value={form.guest_phone} onChange={(e) => setField("guest_phone", e.target.value)} /></div>
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block"># Guests</label><input type="number" min="1" className={inputCls} value={form.num_guests} onChange={(e) => setField("num_guests", e.target.value)} /></div>
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Check-in</label><input data-testid="booking-checkin-input" type="date" required className={inputCls} value={form.check_in} onChange={(e) => setField("check_in", e.target.value)} /></div>
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Check-out</label><input data-testid="booking-checkout-input" type="date" required className={inputCls} value={form.check_out} onChange={(e) => setField("check_out", e.target.value)} /></div>
              <div><label className="text-sm font-medium text-gray-700 mb-1.5 block">Total price ($)</label><input type="number" min="0" step="0.01" className={inputCls} value={form.total_price} onChange={(e) => setField("total_price", e.target.value)} /></div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Status</label>
                <select className={inputCls} value={form.status} onChange={(e) => setField("status", e.target.value)}>
                  <option value="confirmed">Confirmed</option><option value="pending">Pending</option><option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Payment</label>
                <select className={inputCls} value={form.payment_status} onChange={(e) => setField("payment_status", e.target.value)}>
                  <option value="unpaid">Unpaid</option><option value="invoiced">Invoiced (GoDaddy)</option><option value="paid">Paid</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">GoDaddy invoice link <span className="text-gray-400 font-normal">(paste payment link to send guest)</span></label>
                <input data-testid="booking-invoice-input" className={inputCls} value={form.godaddy_invoice_url} onChange={(e) => setField("godaddy_invoice_url", e.target.value)} placeholder="https://invoice.godaddy.com/..." />
              </div>
              <div className="sm:col-span-2"><label className="text-sm font-medium text-gray-700 mb-1.5 block">Notes</label><textarea rows="2" className={inputCls} value={form.notes} onChange={(e) => setField("notes", e.target.value)} /></div>
              <div className="sm:col-span-2 flex justify-end gap-3 mt-2">
                <button type="button" onClick={() => setOpen(false)} className="bg-[#F4F1EA] text-[#1A365D] hover:bg-[#E3D5CA] rounded-full px-6 py-2.5 font-medium transition-colors">Cancel</button>
                <button data-testid="save-booking-button" type="submit" disabled={saving} className="bg-[#1A365D] text-white hover:bg-[#2A4A7F] rounded-full px-6 py-2.5 font-medium transition-colors flex items-center gap-2 disabled:opacity-60">
                  {saving && <Loader2 className="animate-spin" size={16} />} Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
